import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { HERO } from "@/constants/testIds";

const BOTTLE_IMG = "/generated/bottle_hero.png";

const ease = [0.22, 1, 0.36, 1];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden"
    >
      {/* soft cream wash */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 right-0 w-[55%] h-[60%] bg-gradient-to-bl from-[#F4F0EA] to-transparent" />
        <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-gradient-to-tr from-[#F4F0EA]/70 to-transparent" />
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-y-16 md:gap-y-0 md:gap-x-8 items-center">
          {/* Left: copy */}
          <div className="col-span-12 md:col-span-7 lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease }}
              data-testid={HERO.badge}
              className="inline-flex items-center gap-3 mb-10 md:mb-12"
            >
              <span className="w-8 h-px bg-[#2C2826]/40" />
              <span className="overline text-[#2C2826]/80">
                EILEA · Pre-lansare 2026
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, ease, delay: 0.05 }}
              data-testid={HERO.title}
              className="font-display text-[2.6rem] leading-[1.15] sm:text-5xl md:text-[3.6rem] lg:text-[4.5rem] text-[#2C2826] tracking-[-0.01em]"
            >
              Confortul unei creme.
              <br />
              <span className="accent-soft">În</span> câteva secunde.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.2 }}
              data-testid={HERO.subtitle}
              className="mt-8 max-w-[34rem] font-body text-base md:text-lg leading-relaxed text-[#5C5652] font-light"
            >
              Un body milk mist inspirat din skincare, creat pentru
              confortul pielii de zi cu zi.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.28 }}
              className="mt-5 max-w-[34rem] font-body text-[15px] md:text-base leading-relaxed text-[#5C5652]/85 font-light"
            >
              Se așază într-un strat fin, oferă confort imediat și lasă
              pielea catifelată, fără reziduu gras.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.32 }}
              data-testid="hero-price-block"
              className="mt-12 max-w-[34rem]"
            >
              <div className="flex items-baseline gap-4">
                <span
                  data-testid="hero-price-old"
                  className="font-body text-base md:text-lg text-[#5C5652]/60 font-light line-through decoration-[0.5px]"
                >
                  229 lei
                </span>
                <span
                  data-testid="hero-price-new"
                  className="font-display text-[1.75rem] md:text-[2rem] leading-none text-[#2C2826] tracking-[-0.01em]"
                >
                  169 lei
                </span>
              </div>
              <p className="mt-3 font-body text-[15px] md:text-base text-[#5C5652] font-light tracking-[0.01em]">
                Preț de lansare pentru primul drop EILEA.
              </p>
              <p className="mt-2 font-body text-xs md:text-[13px] text-[#5C5652]/70 font-light tracking-[0.02em]">
                Valabil exclusiv pentru primele comenzi.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.42 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <a
                href="#waitlist"
                data-testid={HERO.ctaPrimary}
                className="btn-ghost group"
              >
                Vreau acces prioritar
                <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
              </a>
              <a
                href="#how"
                data-testid={HERO.ctaSecondary}
                className="btn-ghost group"
              >
                Descoperă produsul
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, ease, delay: 0.6 }}
              className="mt-16"
            >
              <p className="text-xs text-[#5C5652] tracking-wide font-light">
                Primul drop EILEA
              </p>
            </motion.div>
          </div>

          {/* Right: bottle mockup */}
          <div className="col-span-12 md:col-span-5 lg:col-span-6 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, ease, delay: 0.1 }}
              className="relative w-full aspect-square max-w-[640px] mx-auto"
            >
              <div className="absolute inset-0 flex items-center justify-center animate-float">
                <div className="relative w-full aspect-square rounded-full overflow-hidden shadow-[0_30px_60px_rgba(60,40,28,0.18)]">
                  <img
                    src={BOTTLE_IMG}
                    alt="EILEA Body Milk Mist bottle"
                    data-testid={HERO.bottleImage}
                    className="absolute inset-0 w-full h-full object-cover scale-[1.15]"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
